import moment from 'moment';
import {
  Dispatch,
  MutableRefObject,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { DomainGq } from '../../../../../graphql/projects/models/DomainGq';
import { KeywordFilteringGq } from '../../../../../graphql/projects/models/KeywordFilteringGq';
import { KeywordPagingGq } from '../../../../../graphql/projects/models/KeywordPagingGq';
import { KeywordSelecting } from '../../../../../graphql/projects/models/KeywordSelectingGq';
import { KeywordSortByTypeGq } from '../../../../../graphql/projects/models/KeywordSortByTypeGq';
import { KeywordSortingGq } from '../../../../../graphql/projects/models/KeywordSortingGq';
import { TagGroupGq } from '../../../../../graphql/projects/models/TagGroupGq';
import { projectsApiService } from '../../../../../graphql/projects/projectsApiService';
import { KeywordTableColumn } from '../KeywordTableColumnPicker/models';
import {
  KeywordColumnPickerBag,
  useKeywordColumnPicker,
} from '../KeywordTableColumnPicker/useKeywordColumnPicker';
import { KeywordSelectingGq } from '../../../../../graphql/projects/models/KeywordSelectingGq';
import { TableUIState } from './TableUIState';
import { VariantState } from './VariantState';
import { getColumnId } from '../KeywordTableColumnPicker/getColumnId';
const deepEqual = require('deep-equal');

export interface KeywordTableState {
  filterState: KeywordFilteringGq;
  setFilterState: Dispatch<SetStateAction<KeywordFilteringGq>>;
  backendFilterState: KeywordFilteringGq;
  backendSelectionState: KeywordSelectingGq;
  resetFilterState: () => void;
  hasFiltering: boolean;

  sortingState: KeywordSortingGq;
  setSortingState: Dispatch<SetStateAction<KeywordSortingGq>>;
  handleSort: (
    sortyByType: KeywordSortByTypeGq,
    sortById?: number,
    direction?: 'asc' | 'desc',
    isProgressPage?: boolean
  ) => void;

  selectionState: KeywordSelecting;
  setSelectionState: Dispatch<SetStateAction<KeywordSelecting>>;
  handleSelectToggle: (id: number) => void;
  handleSelectMany: (ids: number[], selecting: boolean) => void;
  handleResetSelection: () => void;

  pagingState: KeywordPagingGq;
  setPagingState: Dispatch<SetStateAction<KeywordPagingGq>>;
  handleResetPaging: () => void;

  tableUIState: TableUIState;
  setTableUIState: Dispatch<SetStateAction<TableUIState>>;

  columnPickerBag: KeywordColumnPickerBag;
  columnHeadRef: MutableRefObject<Record<string, HTMLElement>>;
  scrollToColumn: (column: KeywordTableColumn) => void;

  variantState: VariantState;
  handleSelectVariant: (keywordId: number, variantId: number) => void;
}

const emptyFilter: KeywordFilteringGq = {
  keyword: {
    searchTerm: '',
  },
  translatedFromKeyword: {
    searchTerm: '',
  },
  translationEnglish: {
    searchTerm: '',
  },
  projectId: 0,
  tagFilters: [],
  tagCountMin: null,
  tagCountMax: null,
  archived: false,
  wordCountExact: null,
  wordCountMin: null,
  serpResultTimeMin: null,
  serpResultTimeMax: null,
  apiCallCollectionIds: [],
  domainRankFilters: [],
  serpStatusFilter: [],
  isProgressPage: false,
  progressRequestIds: [],
};

export function useKeywordTableState(
  initialColumnState: KeywordTableColumn[],
  initialFilterState?: Partial<KeywordFilteringGq> & { projectId: number },
  initialSelectionState?: KeywordSelecting,
  initialUIState?: Partial<TableUIState>,
  domains?: DomainGq[],
  useAutomaticDomains?: boolean,
  tagGroups?: TagGroupGq[],
  initialPaginationState?: Partial<KeywordPagingGq>,
  hasTranslations?: boolean,
  hasEnglishTranslations?: boolean,
  onFilterReset?: () => void
): KeywordTableState {
  // Filtering
  const [filterState, setFilterState] = useState<KeywordFilteringGq>({
    ...emptyFilter,
    ...initialFilterState,
  });

  // Logic for resetting initial filter state
  // This should only happen in case it's new
  const [previousStartFilterState, setPreviousStartFilterState] = useState(initialFilterState);
  useEffect(() => {
    // Check if it's actually new at a granular level, and not only reference equality
    // deepEqual helps with that
    if (deepEqual(previousStartFilterState, initialFilterState)) {
      return;
    }
    // If new - reset the filters
    setPreviousStartFilterState(initialFilterState);
    setFilterState((prev) => ({ ...prev, ...initialFilterState }));
  }, [initialFilterState]);

  const [hasFiltering, setHasFiltering] = useState(false);

  useEffect(() => {
    if (
      !deepEqual(
        {
          ...emptyFilter,
          ...previousStartFilterState,
        },
        { ...filterState, archived: false }
      )
    ) {
      setHasFiltering(true);
    } else setHasFiltering(false);
  }, [filterState, previousStartFilterState]);

  const resetFilterState = useCallback(() => {
    setFilterState({
      ...emptyFilter,
      ...previousStartFilterState,
    });
    onFilterReset !== undefined && onFilterReset();
  }, [previousStartFilterState]);

  const backendFilterState = useMemo(
    (): KeywordFilteringGq => ({
      ...filterState,
      volumeShareMin:
        filterState.volumeShareMin !== undefined ? +filterState.volumeShareMin / 100 : undefined,
      volumeShareMax:
        filterState.volumeShareMax !== undefined ? +filterState.volumeShareMax / 100 : undefined,
      cpcMin: filterState.cpcMin !== undefined ? +filterState.cpcMin * 1000000 : undefined,
      cpcMax: filterState.cpcMax !== undefined ? +filterState.cpcMax * 1000000 : undefined,
      competitionMin:
        filterState.competitionMin != undefined ? +filterState.competitionMin : undefined,
      competitionMax:
        filterState.competitionMax !== undefined ? +filterState.competitionMax : undefined,
      serpResultTimeMin:
        filterState.serpResultTimeMin !== null
          ? moment.utc(filterState.serpResultTimeMin).format('YYYY-MM-DD')
          : null,
      serpResultTimeMax:
        filterState.serpResultTimeMax !== null
          ? moment.utc(filterState.serpResultTimeMax).add(1, 'days').format('YYYY-MM-DD')
          : null,
    }),
    [filterState]
  );

  // Selection
  const [selectionState, setSelectionState] = useState<KeywordSelecting>(
    initialSelectionState ?? {
      invertSelection: false,
      keywordIds: {},
      emptyIsAll: true,
    }
  );

  const handleSelectToggle = useCallback(
    (id: number) =>
      setSelectionState((prevSelectionState) => {
        let keywordIds = { ...prevSelectionState.keywordIds };

        const keywordIsSelected = !!keywordIds[id];
        if (!keywordIsSelected) {
          keywordIds[id] = true;
        } else if (!keywordIsSelected) {
          delete keywordIds[id];
        }

        return { ...prevSelectionState, keywordIds: keywordIds };
      }),
    []
  );

  const handleSelectMany = useCallback(
    (ids: number[], selecting: boolean) =>
      setSelectionState((prevSelectionState) => {
        let keywordIds = { ...prevSelectionState.keywordIds };
        let dataChanged = false;
        selecting = prevSelectionState.invertSelection ? !selecting : selecting;
        ids.forEach((id) => {
          const keywordIsSelected = !!keywordIds[id];
          if (selecting && !keywordIsSelected) {
            // Add selection
            keywordIds[id] = true;
            dataChanged = true;
          } else if (!selecting && keywordIsSelected) {
            // Remove selection
            delete keywordIds[id];
            dataChanged = true;
          }
        });

        if (!dataChanged) {
          return prevSelectionState;
        }

        return { ...prevSelectionState, keywordIds: keywordIds };
      }),
    []
  );

  const handleResetSelection = useCallback(
    () =>
      setSelectionState(
        initialSelectionState ?? {
          invertSelection: false,
          keywordIds: [],
          emptyIsAll: true,
        }
      ),
    []
  );

  // Handle reset selection when relevant
  useEffect(() => {
    handleResetSelection();
  }, [filterState]);

  const backendSelectionState = useMemo(
    (): KeywordSelectingGq => ({
      invertSelection: selectionState.invertSelection,
      emptyIsAll: selectionState.emptyIsAll,
      keywordIds: Object.keys(selectionState.keywordIds).map((k) => +k),
    }),
    [selectionState]
  );

  // Sorting
  const [sortingState, setSortingState] = useState<KeywordSortingGq>({
    sortByType: KeywordSortByTypeGq.VOLUME,
    sortDirection: 'desc',
  });

  // Logic for flipping the sorting direction
  const handleSort = useCallback(
    (sortByType: KeywordSortByTypeGq, sortById?: number, direction?: 'asc' | 'desc') =>
      setSortingState((prevSort) => {
        let sortDirection: 'asc' | 'desc' | undefined = 'asc';

        // Specific direction flip
        if (!!direction) {
          const newDirection = direction !== prevSort.sortDirection;
          const newType = sortByType !== prevSort.sortByType || sortById !== prevSort.sortById;
          sortDirection = newDirection || newType ? direction : undefined;
        }
        // General toggle
        else if (prevSort.sortByType === sortByType && prevSort.sortById === sortById) {
          // Flip direction
          sortDirection = prevSort.sortDirection === 'asc' ? 'desc' : 'asc';
        }

        return {
          sortDirection,
          sortByType,
          sortById,
        };
      }),
    []
  );

  // Paging
  // Amount per page is stored in local storage - so it persists over time
  const [pagingState, setPagingState] = useState<KeywordPagingGq>({
    page: 0,
    amountPerPage: localStorage.getItem('amountPerPage')
      ? Math.max(10, +localStorage.getItem('amountPerPage'))
      : 100,
    ...initialPaginationState,
  });

  useEffect(() => {
    localStorage.setItem('amountPerPage', pagingState.amountPerPage.toString());
  }, [pagingState.amountPerPage]);

  const handleResetPaging = useCallback(
    () =>
      setPagingState((prev) => ({
        ...prev,
        page: 0,
      })),
    []
  );

  // Reset paging when relevant
  useEffect(
    () => handleResetPaging(),
    [
      filterState,
      pagingState.amountPerPage,
      sortingState.sortById,
      sortingState.sortByType,
      sortingState.sortDirection,
    ]
  );

  // UI
  const [tableUIState, setTableUIState] = useState<TableUIState>({
    colorRanks: false,
    enableColor: false,
    showDomainsPanel: false,
    showTagPanel: false,
    ...initialUIState,
  });

  // Columns
  const columnPickerBag = useKeywordColumnPicker(
    initialColumnState,
    domains,
    useAutomaticDomains,
    tagGroups,
    hasTranslations,
    hasEnglishTranslations
  );

  // Variants
  const [variantState, setVariantState] = useState<VariantState>({
    loadingKeywords: {},
    localVariants: {},
  });

  const variantMutation = projectsApiService.mutations.projects.useSelectKeywordVariant();
  const handleSelectVariant = useCallback((keywordId: number, variantId: number) => {
    setVariantState((prev) => {
      const next = { ...prev };

      // Instantly set variant in UI
      next.localVariants = { ...prev.localVariants };
      next.localVariants[keywordId] = variantId;

      // Make the variant dialog show loading indicator
      next.loadingKeywords = { ...prev.loadingKeywords };
      next.loadingKeywords[keywordId] = true;
      return next;
    });

    // Perform mutation
    return variantMutation({
      projectId: initialFilterState.projectId,
      variantId: variantId,
    }).finally(() => {
      setVariantState((prev) => {
        const next = { ...prev };
        // Remove loading indicator
        next.loadingKeywords = { ...prev.loadingKeywords };
        next.loadingKeywords[keywordId] = false;
        return next;
      });
    });
  }, []);

  const columnHeadRef = useRef<Record<string, HTMLElement>>({});
  const scrollToColumn = useCallback((column: KeywordTableColumn) => {
    setTimeout(() => {
      const columnElement = columnHeadRef.current[getColumnId(column)];
      let currentRef = columnElement;
      let tableRef = undefined;
      let iterationCounter = 0;
      while (tableRef === undefined && iterationCounter <= 8 && currentRef !== undefined) {
        iterationCounter += 1;

        if (currentRef?.hasAttribute('data-scroll-container')) {
          const columnLeftPosition = columnElement.offsetLeft;
          const columnRightPosition = columnElement.offsetLeft + columnElement.clientWidth;

          const tableLeftBound = currentRef.scrollLeft;
          const tableRightBound = currentRef.scrollLeft + currentRef.clientWidth;

          const columnNotVisible =
            tableLeftBound > columnLeftPosition || tableRightBound < columnRightPosition;

          if (columnNotVisible) {
            const leftScroll = columnRightPosition - currentRef.clientWidth;
            currentRef.scrollTo({ left: leftScroll, behavior: 'smooth' });
          }

          break;
        } else {
          currentRef = currentRef?.parentElement;
        }
      }
    });
  }, []);

  return useMemo(
    () => ({
      scrollToColumn,
      hasFiltering,
      columnHeadRef,
      resetFilterState,
      filterState,
      setFilterState,
      backendFilterState,
      selectionState,
      setSelectionState,
      handleResetSelection,
      handleSelectToggle,
      columnPickerBag,
      handleSelectMany,
      pagingState,
      handleResetPaging,
      setPagingState,
      sortingState,
      setSortingState,
      handleSort,
      tableUIState,
      setTableUIState,
      backendSelectionState,
      handleSelectVariant,
      variantState,
    }),
    [
      pagingState,
      columnPickerBag,
      filterState,
      backendFilterState,
      sortingState,
      selectionState,
      tableUIState,
      variantState,
    ]
  );
}
